import { Alpine } from '../core/alpine-core.js';
import { apiFetch } from './api.js';
import { CURRENT_SEASON } from '../core/current-season.js';
import { getActiveLeagueId, getLeagueIdFromPath, withLeagueId } from './league-context.js';

function numberOrZero(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

Alpine.data('leagueDetailPage', () => ({
  loading: true,
  refreshSpinning: false,
  firstLoad: true,
  saving: false,
  error: null,
  saveError: null,
  leagueKey: '',
  season: CURRENT_SEASON,
  league: null,
  overview: null,
  schedule: [],
  editOpen: false,
  form: {
    name: '',
    season: CURRENT_SEASON,
    description: '',
  },

  leagueDisplayLabel() {
    const name = String(this.league?.name || '').trim();
    return name || this.leagueKey || 'PDL';
  },

  async init() {
    this.leagueKey = getLeagueIdFromPath() || getActiveLeagueId('PDL');
    await this.loadAll();
    this.firstLoad = false;
    this._onLeagueNav = () => {
      const next = getLeagueIdFromPath() || getActiveLeagueId('PDL');
      if (next === this.leagueKey) return;
      this.leagueKey = next;
      this.loadAll();
    };
    document.addEventListener('pinecone:end', this._onLeagueNav);
    this.$root?.addEventListener?.('alpine:destroy', () => {
      document.removeEventListener('pinecone:end', this._onLeagueNav);
    });
  },

  async loadSchedule(season = this.season) {
    const seasonParam = String(season || '').trim();
    const qs = seasonParam ? `?season=${encodeURIComponent(seasonParam)}` : '';
    const scheduleRes = await apiFetch(`/leagues/${encodeURIComponent(this.leagueKey)}/schedule${qs}`);
    const scheduleData = await scheduleRes.json().catch(() => ({}));
    if (!scheduleRes.ok || scheduleData?.ok === false) {
      throw new Error(scheduleData?.message || 'Failed to load schedule');
    }
    let items = Array.isArray(scheduleData.items) ? scheduleData.items : [];
    if (!items.length && seasonParam) {
      const fallbackRes = await apiFetch(`/leagues/${encodeURIComponent(this.leagueKey)}/schedule`);
      const fallbackData = await fallbackRes.json().catch(() => ({}));
      if (fallbackRes.ok && fallbackData?.ok !== false && Array.isArray(fallbackData.items) && fallbackData.items.length) {
        items = fallbackData.items;
      }
    }
    this.schedule = items;
  },

  async loadAll() {
    this.loading = true;
    this.error = null;
    try {
      const seasonQs = this.season ? `?season=${encodeURIComponent(this.season)}` : '';
      const leagueRes = await apiFetch(`/leagues/${encodeURIComponent(this.leagueKey)}${seasonQs}`);
      const leagueData = await leagueRes.json().catch(() => ({}));
      if (!leagueRes.ok || leagueData?.ok === false) {
        throw new Error(leagueData?.message || 'Failed to load league');
      }
      this.league = leagueData.league || null;
      this.overview = leagueData.overview || null;
      if (this.league?.season) this.season = String(this.league.season);
      await this.loadSchedule(this.season);
      this.form = {
        name: String(this.league?.name || this.leagueKey),
        season: String(this.league?.season || this.season),
        description: String(this.league?.description || ''),
      };
    } catch (e) {
      this.error = e?.message || 'Failed to load league';
      this.league = null;
      this.overview = null;
      this.schedule = [];
    } finally {
      this.loading = false;
      this.refreshSpinning = false;
    }
  },

  async manualRefresh() {
    this.refreshSpinning = true;
    await this.loadAll();
  },

  canEdit() {
    try {
      return Alpine.store('session')?.hasRole?.('admin') || Alpine.store('session')?.hasRole?.('manager');
    } catch {
      return false;
    }
  },

  openEdit() {
    if (!this.canEdit()) return;
    this.saveError = null;
    this.editOpen = true;
  },

  closeEdit() {
    this.editOpen = false;
    this.saveError = null;
  },

  async saveLeague() {
    if (!this.canEdit()) return;
    this.saving = true;
    this.saveError = null;
    try {
      const res = await apiFetch(`/admin/leagues/${encodeURIComponent(this.leagueKey)}`, {
        method: 'PATCH',
        body: {
          name: this.form.name,
          season: this.form.season,
          description: this.form.description,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) {
        throw new Error(data?.message || 'Failed to save league');
      }
      this.league = data.league || this.league;
      this.season = String(this.form.season || this.season);
      this.editOpen = false;
      this.$store?.toastr?.success?.('League updated');
      await this.loadAll();
    } catch (e) {
      this.saveError = e?.message || 'Failed to save league';
      this.$store?.toastr?.error?.(this.saveError);
    } finally {
      this.saving = false;
    }
  },

  fixturesHref() {
    return withLeagueId('/admin/fixtures', this.leagueKey);
  },

  leagueTableHref() {
    return withLeagueId('/admin/league-table', this.leagueKey);
  },

  leagueBuilderHref() {
    return '/admin/league-builder';
  },

  itemDetailHref(item) {
    if (!item || String(item.type || 'fixture') !== 'fixture') return '';
    const id = String(item._id || '').trim();
    if (!id) return '';
    return withLeagueId(`/admin/fixtures/${encodeURIComponent(id)}`, this.leagueKey);
  },

  itemEditHref(item) {
    if (!item || !this.canEdit()) return '';
    const id = String(item._id || '').trim();
    if (!id) return '';
    return withLeagueId(`/admin/fixtures/${encodeURIComponent(id)}/edit`, this.leagueKey);
  },

  newFixtureHref() {
    return withLeagueId('/admin/fixtures/new', this.leagueKey);
  },

  statusBadge() {
    const status = String(this.overview?.status || 'draft');
    const map = {
      draft: { label: this.overview?.statusLabel || 'Draft', cls: 'border-neutral-400/40 bg-neutral-500/15 text-neutral-200' },
      setup: { label: this.overview?.statusLabel || 'Setup', cls: 'border-sky-400/40 bg-sky-500/15 text-sky-200' },
      upcoming: { label: this.overview?.statusLabel || 'Upcoming', cls: 'border-amber-400/40 bg-amber-500/15 text-amber-200' },
      active: { label: this.overview?.statusLabel || 'In progress', cls: 'border-emerald-400/40 bg-emerald-500/15 text-emerald-200' },
      completed: { label: this.overview?.statusLabel || 'Completed', cls: 'border-violet-400/40 bg-violet-500/15 text-violet-200' },
    };
    return map[status] || map.draft;
  },

  leaderLabel() {
    if (this.overview?.seasonWinner) return 'Season winner';
    if (this.overview?.status === 'completed') return 'Final standings';
    return 'Currently leading';
  },

  leaderRow() {
    return this.overview?.seasonWinner || this.overview?.leader || null;
  },

  progressPct() {
    return numberOrZero(this.overview?.progressPct);
  },

  fixtureMatchLabel(item) {
    if (!item) return '';
    const home = item.homeTeamName || item.homeTeam?.name || 'Home';
    const away = item.awayTeamName || item.awayTeam?.name || 'Away';
    return `${home} vs ${away}`;
  },

  fixtureScoreLabel(item) {
    if (!item) return '';
    if (item.forfeit) return 'Forfeit';
    const home = numberOrZero(item.legsHome);
    const away = numberOrZero(item.legsAway);
    if (item.legsHome != null && item.legsAway != null) return `${home} – ${away}`;
    if (item.winnerTeamName) return `${item.winnerTeamName} won`;
    return String(item.status || 'scheduled');
  },

  fixtureWinnerLabel(item) {
    if (!item) return '';
    if (item.forfeit) return item.winnerTeamName ? `${item.winnerTeamName} (forfeit)` : 'Forfeit';
    if (item.winnerTeamName) return item.winnerTeamName;
    const home = numberOrZero(item.legsHome);
    const away = numberOrZero(item.legsAway);
    if (item.legsHome != null && item.legsAway != null) {
      if (home > away) return item.homeTeamName || 'Home';
      if (away > home) return item.awayTeamName || 'Away';
      return 'Draw';
    }
    return '';
  },

  rowLabel(item) {
    if (!item) return '';
    if (item.type === 'event' || item.type === 'break') {
      return item.title || item.roundLabel || (item.type === 'break' ? 'Break' : 'Event');
    }
    return this.fixtureMatchLabel(item);
  },

  rowSubLabel(item) {
    if (!item) return '';
    if (item.type === 'fixture') return item.roundLabel || '';
    return item.venueName || item.venue?.name || '';
  },

  typeBadge(item) {
    const t = String(item?.type || 'fixture');
    if (t === 'event') return { label: 'Event', cls: 'border-emerald-400/40 bg-emerald-500/15 text-emerald-200' };
    if (t === 'break') return { label: 'Break', cls: 'border-amber-400/40 bg-amber-500/15 text-amber-200' };
    return { label: 'Fixture', cls: 'border-sky-400/40 bg-sky-500/15 text-sky-200' };
  },

  statusTone(status) {
    const s = String(status || 'scheduled').toLowerCase();
    if (s === 'completed' || s === 'finished') return 'text-emerald-300 border-emerald-400/30 bg-emerald-500/10';
    if (s === 'live' || s === 'in_progress' || s === 'started') return 'text-rose-200 border-rose-400/30 bg-rose-500/10';
    if (s === 'cancelled' || s === 'canceled') return 'text-neutral-400 border-neutral-500/30 bg-neutral-500/10';
    return 'text-neutral-300 border-white/10 bg-white/5';
  },

  formatDateTime(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('en-GB', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  },

  formatDay(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-GB', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  },

  formToken(team) {
    return `${numberOrZero(team?.wins)}-${numberOrZero(team?.draws)}-${numberOrZero(team?.losses)}`;
  },

  scheduleByDay() {
    const byDay = new Map();
    for (const item of this.schedule) {
      const key = String(item.startAt || '').slice(0, 10);
      if (!key) continue;
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key).push(item);
    }
    return [...byDay.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, items]) => ({ date, items }));
  },
}));
